import { useEffect, useState } from "react";
import styled from "styled-components";
import { colors } from "../../../../styles/color-tokens";
import { useDispatch, useSelector } from "react-redux";

import { Modal } from "../../../../components/modal/modal";
import { hideModal } from "../../../../actions/modal";

import { FormSize, SubmitButton } from "../../../../ui/ecosystems/forms";
import { getUser } from "../../../../selectors/commonSelectors";
import { useUserCurrency } from "../../../../entities/current-user";
import { useTrackEvent } from "../../../../libs/analytics";
import { SKED_PLAN } from "../../../../types/app";
import {
  PDF_CONTACT_BUTTON,
  PDF_UPSELL_MODAL,
  SAMPLE_REPORT_DOWNLOAD,
} from "../../../../constants/Events";
import { openSupportChat } from "../../../../libs/support";
import { Link } from "../../../../ui/Link";
import { Flex } from "../../../../ui/commonStyled/layout";
import { saveFile } from "../../../../utils/file";
import { Spinner } from "../../../../components/ui/spinner";

const pdfAddonPrices = {
  AUD: {
    [SKED_PLAN.FUNDAMENTALS]: "$6 AUD/mo",
    [SKED_PLAN.FUNDAMENTALS_ANNUAL]: "$60 AUD/year",
    [SKED_PLAN.ESSENTIALS]: "24 AUD/mo",
    [SKED_PLAN.ESSENTIALS_ANNUAL]: "$240 AUD /year",
  },
  USD: {
    [SKED_PLAN.FUNDAMENTALS]: "4 USD/mo",
    [SKED_PLAN.FUNDAMENTALS_ANNUAL]: "40 USD/year",
    [SKED_PLAN.ESSENTIALS]: "$16 USD/mo",
    [SKED_PLAN.ESSENTIALS_ANNUAL]: "$160 USD/year",
  },
  GBP: {
    [SKED_PLAN.FUNDAMENTALS]: "£3 GBP/mo",
    [SKED_PLAN.FUNDAMENTALS_ANNUAL]: "£30 GBP/year",
    [SKED_PLAN.ESSENTIALS]: "£13 GBP/mo",
    [SKED_PLAN.ESSENTIALS_ANNUAL]: "£130 GBP/year",
  },
  EUR: {
    [SKED_PLAN.FUNDAMENTALS]: "€4 EUR/mo",
    [SKED_PLAN.FUNDAMENTALS_ANNUAL]: "€40/year",
    [SKED_PLAN.ESSENTIALS]: "€15 EUR/mo",
    [SKED_PLAN.ESSENTIALS_ANNUAL]: "$150 EUR/year",
  },
};

type PdfAddonCurrency = keyof typeof pdfAddonPrices;
type PdfAddonPlan =
  | SKED_PLAN.FUNDAMENTALS
  | SKED_PLAN.FUNDAMENTALS_ANNUAL
  | SKED_PLAN.ESSENTIALS
  | SKED_PLAN.ESSENTIALS_ANNUAL;

export const AddInsightsReportsModal = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleHideModal = () => {
    dispatch(hideModal());
  };

  const user = useSelector(getUser);
  const userCurrency = useUserCurrency();
  const trackEvent = useTrackEvent();

  const upgradeablePlans: PdfAddonPlan[] = [
    SKED_PLAN.FUNDAMENTALS,
    SKED_PLAN.FUNDAMENTALS_ANNUAL,
    SKED_PLAN.ESSENTIALS,
  ];
  const userPlan = user?.plan as PdfAddonPlan | undefined;
  const currency: PdfAddonCurrency =
    userCurrency in pdfAddonPrices ? (userCurrency as PdfAddonCurrency) : "USD";
  const isAvailableForUpgrade =
    userPlan !== undefined && upgradeablePlans.includes(userPlan);

  const amount = isAvailableForUpgrade
    ? pdfAddonPrices[currency][userPlan]
    : pdfAddonPrices["USD"][SKED_PLAN.FUNDAMENTALS_ANNUAL];

  const submitUpgrade = () => {
    trackEvent({
      eventName: PDF_CONTACT_BUTTON,
      useServices: ["AMP"],
    });
    openSupportChat("Hey, I’d like to add standard PDF reports to my plan.");
  };

  const handleDownloadPdf = async () => {
    if (!isLoading) {
      setIsLoading(true);
      await saveFile(
        "https://schedugram-client-staging.s3.amazonaws.com/assets/resources/downloads/Instagram+Insights+Report+.pdf",
        "Instagram+Insights+Report+.pdf"
      );
      trackEvent({
        eventName: SAMPLE_REPORT_DOWNLOAD,
        useServices: ["AMP"],
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    trackEvent({
      eventName: PDF_UPSELL_MODAL,
      useServices: ["AMP"],
    });
  }, []);

  return (
    <ModalComponent show={true} onHide={handleHideModal}>
      <ModalBody>
        <Modal.Header closeButton onHide={handleHideModal}>
          <Modal.Title>
            Get ready-to-share standard PDF reports with an easy upgrade
          </Modal.Title>
          <p className="text-sm text-neutral-600 mt-2">
            Polished reporting with all the important metrics you need in one
            click.
          </p>
        </Modal.Header>
        <Modal.Body>
          <Container direction="row">
            <Column>
              <Preview src="/assets/img/gif/Splash graphic.gif" />
            </Column>
            <Column>
              <p>
                Save hours every month building manual reports. Download
                presentation-perfect PDF analytics for Instagram, Facebook,
                LinkedIn and Google Business Profile in one click. More
                platforms to come over time.
              </p>
              <p>Add Standard Reporting to your plan for {amount}.</p>
              <LinkStyled color="primary" onClick={handleDownloadPdf}>
                Download a sample report here.{" "}
                {isLoading && (
                  <Spinner size="sm" className="inline-block ml-2" />
                )}
              </LinkStyled>
            </Column>
          </Container>
          <ModalActionsStyled>
            <SubmitButtonStyled
              primary
              size={FormSize.large}
              isSubmitting={false}
              onClick={submitUpgrade}
            >
              Yes, I&apos;d like to add-on or upgrade
            </SubmitButtonStyled>
          </ModalActionsStyled>
        </Modal.Body>
      </ModalBody>
    </ModalComponent>
  );
};

const ModalComponent = styled(Modal)`
  font-size: 14px;
  max-width: 975px;
`;

const ModalBody = styled.div`
  min-width: 600px;
  color: ${colors.neutral700};

  h2 {
    color: ${colors.neutral800};
  }
`;

const Preview = styled.img`
  width: 100%;
  margin: 0 auto;
`;

const Container = styled(Flex)`
  align-items: stretch;
  justify-content: center;
  margin-top: 43px;
  gap: 38px;
`;
const Column = styled(Flex)`
  width: 410px;
  flex-direction: column;
  align-items: stretch;
`;

const LinkStyled = styled(Link)`
  position: relative;
  font-size: 16px;
  text-align: left;
`;

const ModalActionsStyled = styled(Modal.Footer)`
  margin-top: 12px;
  justify-content: flex-end;
`;

const SubmitButtonStyled = styled(SubmitButton)`
  font-weight: bold;
`;
